import { AgGridReact } from "@jsq/ds/core";
import { TransactionRowItem } from "../../types";
import { formatTransactionStatus } from "../../utils";

// Column filters disabled: FilterPanel owns filtering, and getRows ignores filterModel.
export const getColumnDefs =
  (): AgGridReact.AgGridReactProps<TransactionRowItem>["columnDefs"] => [
    {
      field: "glTransactionId",
      headerName: "GL Transaction ID",
      sortable: true,
      filter: false,
      pinned: "left",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "entityId",
      headerName: "Entity ID",
      sortable: true,
      filter: false,
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: { value: number | null }) => params.value ?? "--",
    },
    {
      field: "effectiveDate",
      headerName: "Effective Date",
      sortable: true,
      minWidth: 130,
      flex: 1,
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      sortable: true,
      valueFormatter: (params) => {
        if (params.value == null) return "--";
        const currency = params.data?.transactionCurrency || "USD";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
        }).format(parseFloat(String(params.value)));
      },
      minWidth: 130,
      flex: 1,
    },
    {
      field: "transactionStatus",
      headerName: "Status",
      sortable: true,
      filter: false,
      valueFormatter: (params) => formatTransactionStatus(params.value),
      minWidth: 130,
      flex: 1,
    },
    {
      field: "transactionCurrency",
      headerName: "Currency",
      sortable: true,
      minWidth: 100,
      flex: 0.7,
    },
    {
      field: "transactionDescription",
      headerName: "Description",
      sortable: true,
      filter: false,
      minWidth: 200,
      flex: 2,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "debitOrCredit",
      headerName: "Debit/Credit",
      sortable: true,
      minWidth: 110,
      flex: 0.8,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "positionId",
      headerName: "Position ID",
      sortable: true,
      minWidth: 110,
      flex: 0.8,
      hide: true,
      cellRenderer: (params: { value: number | null }) => params.value ?? "--",
    },
    {
      field: "glDate",
      headerName: "GL Date",
      sortable: true,
      minWidth: 120,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "totalConvertedCreditAmount",
      headerName: "Converted Credit Amount",
      sortable: true,
      valueFormatter: (params) => {
        if (params.value == null) return "--";
        const currency = params.data?.transactionCurrency || "USD";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
        }).format(parseFloat(String(params.value)));
      },
      minWidth: 180,
      flex: 1,
      hide: true,
    },
    {
      field: "transactionCount",
      headerName: "Txn Count",
      sortable: true,
      filter: false,
      minWidth: 110,
      flex: 0.7,
      hide: true,
    },
    {
      field: "totalShares",
      headerName: "Total Shares",
      sortable: true,
      valueFormatter: (params) => {
        if (params.value == null) return "--";
        return new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }).format(parseFloat(String(params.value)));
      },
      minWidth: 130,
      flex: 1,
      hide: true,
    },
    {
      field: "totalQuantity",
      headerName: "Total Quantity",
      sortable: true,
      valueFormatter: (params) => {
        if (params.value == null) return "--";
        return new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }).format(parseFloat(String(params.value)));
      },
      minWidth: 130,
      flex: 1,
      hide: true,
    },
    {
      field: "avgCostPerShare",
      headerName: "Avg Cost/Share",
      sortable: true,
      valueFormatter: (params) => {
        if (params.value == null) return "--";
        const currency = params.data?.transactionCurrency || "USD";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        }).format(parseFloat(String(params.value)));
      },
      minWidth: 140,
      flex: 1,
      hide: true,
    },
    {
      field: "jsqPositionIds",
      headerName: "JSQ Position IDs",
      sortable: false,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: number[] | null }) => {
        if (!params.value || params.value.length === 0) return "--";
        return params.value.join(", ");
      },
    },
    {
      field: "bankAccount",
      headerName: "Bank Account",
      sortable: true,
      minWidth: 130,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "vendor",
      headerName: "Vendor",
      sortable: true,
      minWidth: 130,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      sortable: true,
      minWidth: 140,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "invoiceDueDate",
      headerName: "Invoice Due Date",
      sortable: true,
      minWidth: 140,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "glAccountNumber",
      headerName: "GL Account Number",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "glAccountType",
      headerName: "GL Account Type",
      sortable: true,
      minWidth: 140,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "transactionType",
      headerName: "GL Transaction Type",
      sortable: true,
      minWidth: 140,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "capitalEventReference",
      headerName: "Capital Event Reference",
      sortable: true,
      minWidth: 180,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "sourcesUses",
      headerName: "Sources/Uses",
      sortable: true,
      minWidth: 120,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchType",
      headerName: "Batch Type",
      sortable: true,
      minWidth: 120,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchCategory",
      headerName: "Batch Category",
      sortable: true,
      minWidth: 130,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchComments",
      headerName: "Batch Comments",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchReference",
      headerName: "Batch Reference",
      sortable: true,
      minWidth: 140,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchSourceObjectId",
      headerName: "Batch Source Object ID",
      sortable: true,
      minWidth: 170,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "batchCreatedAt",
      headerName: "Batch Created At",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "relatedParty",
      headerName: "Related Party",
      sortable: true,
      minWidth: 130,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "dealName",
      headerName: "Deal Name",
      sortable: true,
      minWidth: 130,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "createdAt",
      headerName: "Created At",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
    },
    {
      field: "modifiedAt",
      headerName: "Modified At",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
    {
      field: "deletedAt",
      headerName: "Deleted At",
      sortable: true,
      minWidth: 150,
      flex: 1,
      hide: true,
      cellRenderer: (params: { value: string | null }) => params.value || "--",
    },
  ];
